import React, { memo, useEffect, useRef, useState } from 'react';
import _get from 'lodash.get';
import { Image } from '../../../../ui-kit';
import FileTypeIcon from '../FileTypeIcon';
import Upload from '../Upload';
import getDarkModeImageProps from '../../utils/getDarkModeImageProps';
import renderPdfThumbnail from '../../utils/renderPdfThumbnail';
import '../ImagePreview/styles/_image-preview.scss';

// Prefer a server-rendered thumbnail (from the transcode pipeline) when present — it
// avoids downloading and rendering the whole PDF in every browser, every session.
function getServerThumbnailUrl(asset) {
  return _get(asset, 'assetFiles.THUMBNAIL.sourceUrl', '');
}

function getOriginalUrl(asset) {
  return _get(asset, 'assetFiles.ORIGINAL.sourceUrl', '');
}

// Stable cache key so a replaced PDF (new updatedAt) busts its old thumbnail, while
// re-mounts of the same asset reuse it. Falls back to the source URL when there's no
// id/timestamp to key on.
function getCacheKey(asset, sourceUrl) {
  const assetId = _get(asset, 'assetId');
  const updatedAt =
    _get(asset, 'updatedAt') || _get(asset, 'assetFiles.ORIGINAL.updatedAt');

  if (!assetId) {
    return sourceUrl;
  }

  return updatedAt ? `${assetId}:${updatedAt}` : `${assetId}:${sourceUrl}`;
}

function PdfPreview({ asset, hideFileTypeIcon, darkMode }) {
  const { uploadStatus, loadedPercent } = asset || {};
  const serverThumbnailUrl = getServerThumbnailUrl(asset);
  const originalUrl = getOriginalUrl(asset);
  const cacheKey = getCacheKey(asset, originalUrl);
  const colourMap = getDarkModeImageProps({ darkMode });

  const containerRef = useRef(null);
  const [rendered, setRendered] = useState(null);
  // Only client-render once the preview scrolls near the viewport, so a grid of PDFs
  // doesn't kick off every render at once. Where IntersectionObserver isn't available
  // (e.g. the test env) fall back to rendering eagerly.
  const [visible, setVisible] = useState(
    typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (visible || serverThumbnailUrl || typeof IntersectionObserver === 'undefined') {
      return void 0;
    }

    const node = containerRef.current;
    if (!node) {
      return void 0;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [visible, serverThumbnailUrl]);

  useEffect(() => {
    // A server thumbnail wins — no need to render the PDF client-side at all.
    if (serverThumbnailUrl || !visible || !originalUrl) {
      return void 0;
    }

    let active = true;
    setRendered(null);

    renderPdfThumbnail(originalUrl, { cacheKey }).
    then((dataUrl) => {
      if (active) {
        setRendered(dataUrl);
      }
    }).
    catch(() => {
      // Rendering failed (e.g. CORS / corrupt PDF) — fall back to the pdf icon,
      // which shows automatically while the thumbnail is null.
    });

    return () => {
      active = false;
    };
  }, [serverThumbnailUrl, originalUrl, visible, cacheKey]);

  const thumbnailUrl = serverThumbnailUrl || rendered;

  return (
    <div className="image-preview" ref={containerRef}>
      <Image
        imageUrl={thumbnailUrl}
        width="100%"
        height="100%"
        backgroundSize="contain"
        defaultImageIcon="pdf"
        defaultIconBackground={colourMap.background}
        defaultIconColour={colourMap.icon} />

      {
      thumbnailUrl && !hideFileTypeIcon ?

      <FileTypeIcon
        className="image-preview__media-icon"
        name="pdf"
        color="white" /> :


      null}

      <Upload
        loadedPercent={loadedPercent}
        uploadStatus={uploadStatus}
        assetId={asset?.assetId} />

    </div>);

}

export default memo(PdfPreview);
